import * as zod from 'zod';

import { MotionChildTCPOption } from '@sb/motion-planning';
import {
  ActionRequiredError,
  MotionSpeed,
  SpeedLimitOption,
  Step,
} from '@sb/remote-control/types';
import type { SpeedProfile } from '@sb/routine-runner/speed-profile';
import type { Target as ArcTarget } from '@sb/routine-runner/Step/Arc/Arguments';
import { Expression, Space } from '@sb/routine-runner/types';

import { calculateSpeedProfileFromStepMotionSpeed } from '../util/calculateSpeedProfileFromStepMotionSpeed';
import convertJavaScriptExpressionToUser from '../util/expressions/convertJavaScriptExpressionToUser';
import { convertPythonExpression } from '../util/expressions/v2';
import { validateMotionSpeed } from '../util/validators';

const TargetKind = zod.union([
  zod.literal('singlePosition'),
  zod.literal('positionList'),
  zod.literal('expression'),
]);

type TargetKind = zod.infer<typeof TargetKind>;

export namespace ArcStep {
  export const name = 'Arc';
  export const description =
    'Move the robot in an arc through two positions in space';
  export const librarySection = Step.LibrarySection.Hidden;
  export const argumentKind = 'Arc';

  export const Arguments = zod.object({
    argumentKind: zod.literal('Arc'),
    motionSpeed: zod.preprocess(
      // Per-arc speed profile is not supported yet. Leave this here for when it is.
      (val) => (val == null ? undefined : val),
      MotionSpeed.optional(),
    ),
    speedLimitOption: SpeedLimitOption.default('PARENT_DEFAULTS'),
    tcpOption: MotionChildTCPOption.default('auto'),
    // Mirrors the runner fields, matching the Waypoint pair so a Move step
    // with mixed waypoint/arc children resolves both the same way. New arcs
    // are seeded with the same mode a new waypoint gets, and the arc settings
    // offer the same tooltip picker writing these fields.
    gripperID: zod.string().optional(),
    stopHere: zod.boolean().default(false),
    // The setting `stopHere` is derived from. Never `.default()`: an arc
    // written without the twin carries its setting on `stopHere`, and a
    // default here would read as the operator turning it off. Read it through
    // `rawOrEffective`.
    stopHereRaw: zod.boolean().optional(),
    maintainStartOrientation: zod.boolean().default(false),
    moveDynamicBaseToReachPosition: zod.boolean().default(true),

    // Targets: One each for "via" and "end" points
    targetKinds: zod
      .tuple([TargetKind, TargetKind])
      .default(['singlePosition', 'singlePosition']),
    positionListIDs: zod
      .tuple([zod.string().nullable(), zod.string().nullable()])
      .default([null, null]),
    positionListIndices: zod
      .tuple([zod.number().nullable(), zod.number().nullable()])
      .default([null, null]),
    targets: zod
      .tuple([Space.Position.nullable(), Space.Position.nullable()])
      .default([null, null]),
    expressions: zod
      .tuple([Expression.nullable(), Expression.nullable()])
      .default([null, null]),
  });

  export type Arguments = zod.infer<typeof Arguments>;

  const createTarget = (
    targetKind: TargetKind,
    positionListID: string | null,
    positionListIndex: number | null,
    target: Space.Position | null,
    expression: Expression | null,
  ): ArcTarget => {
    switch (targetKind) {
      case 'positionList':
        if (positionListID) {
          return {
            positionListID,
            positionListIndex: positionListIndex ?? undefined,
          };
        }

        break;

      case 'expression':
        if (expression) {
          return { expression };
        }

        break;

      default:
        if (target) {
          return target;
        }
    }

    throw new ActionRequiredError({
      kind: 'invalidConfiguration',
      message: 'Target is not configured',
    });
  };

  export const toRoutineRunner: Step.ToRoutineRunner = ({
    stepConfiguration: { args },
    stepData,
    baseSpeedProfile,
  }) => {
    if (args?.argumentKind !== argumentKind) {
      throw new TypeError(`Expected argument kind ${argumentKind}`);
    }

    const {
      motionSpeed,
      speedLimitOption,
      tcpOption,
      gripperID,
      stopHere,
      maintainStartOrientation,
      positionListIDs,
      positionListIndices,
      targets: targetsRaw,
      targetKinds,
      expressions,
    } = args;

    validateMotionSpeed(name, motionSpeed);

    const speedProfile: SpeedProfile = calculateSpeedProfileFromStepMotionSpeed(
      {
        motionSpeed,
        baseSpeedProfile,
      },
    );

    const targets = positionListIDs.map((_, i) =>
      createTarget(
        targetKinds[i],
        positionListIDs[i],
        positionListIndices[i],
        targetsRaw[i],
        expressions[i],
      ),
    ) as [ArcTarget, ArcTarget];

    return {
      ...stepData,
      stepKind: 'Arc',
      args: {
        targets,
        stopHere,
        maintainStartOrientation,
        speedProfile,
        useParentSpeedProfile: speedLimitOption === 'PARENT_DEFAULTS',
        tcpOption,
        gripperID,
      },
    };
  };

  const getTargetDescription = (
    targetKind: TargetKind,
    positionListID: string | null,
    positionListIndex: number | null,
    expression: Expression | null,
    spaceItems: Space.Item[],
  ) => {
    if (targetKind === 'singlePosition') {
      return 'manually specified position';
    }

    if (targetKind === 'positionList') {
      const space = spaceItems.find((item) => item.id === positionListID);

      if (space == null) {
        return null;
      }

      if (space.kind === 'singlePosition') {
        return space.name;
      }

      if (space.kind === 'safeHomePose') {
        return 'home pose';
      }

      if (
        space.kind === 'freeformPositionList' ||
        space.kind === 'gridPositionList'
      ) {
        if (positionListIndex == null) {
          return `the next position in ${space.name}`;
        }

        return `position ${positionListIndex + 1} in ${space.name}`;
      }

      return null;
    }

    if (targetKind === 'expression') {
      if (expression == null) {
        return null;
      }

      if (expression.kind === 'JavaScript') {
        // This looks pretty gnarly for most expressions. Probably want to allow
        // the user to build up the expression using a DSL that we can desugar
        // into JS later.
        const jsExprStr = convertJavaScriptExpressionToUser(
          expression.expression,
          { isForStepDescription: true },
        );

        return `a point based on expression ${jsExprStr}`;
      }

      if (expression.kind === 'Python') {
        const { code: pyExprStr } =
          convertPythonExpression.withRemoteControlVariables.toUserDescription(
            expression.expression,
          );

        return `a point based on expression ${pyExprStr}`;
      }

      return null;
    }

    return null;
  };

  export const getStepDescription: Step.GetStepDescription = ({
    stepConfiguration: { args },
    routine,
    includeStepName,
    globalSpace,
  }) => {
    if (args?.argumentKind !== argumentKind || !routine) {
      return null;
    }

    const allSpaces = [...(globalSpace ?? []), ...routine.space];

    const targetDescriptions = args.targetKinds.map((_, i) =>
      getTargetDescription(
        args.targetKinds[i],
        args.positionListIDs[i],
        args.positionListIndices[i],
        args.expressions[i],
        allSpaces,
      ),
    ) as [string | null, string | null];

    if (targetDescriptions[0] == null || targetDescriptions[1] == null) {
      return null;
    }

    const parts: string[] = [];

    if (includeStepName) {
      parts.push(`${name} `);
    }

    parts.push(
      ...['through', targetDescriptions[0], 'to', targetDescriptions[1]],
    );

    return parts.join(' ');
  };
}

ArcStep satisfies Step.StepKindInfo;
